import React from 'react';
import { motion } from 'framer-motion';
import { Server, Code, Laptop, Shield, AlertTriangle, Award, Zap, BarChart3, Globe } from 'lucide-react';

const CmsInfoComparison: React.FC = () => {
  // Données de test pour les 3 options
  const mockCmsData = {
    name: 'WordPress',
    version: '4.7.30',
    confidence: 90,
    description: 'Site sous WordPress 4.7.30, CMS populaire et puissant.',
    logo_url: 'https://s.w.org/style/images/about/WordPress-logotype-standard.png'
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* En-tête */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Comparaison des designs CMS Info
          </h1>
          <p className="text-xl text-gray-600">
            Trois propositions modernes pour la section "Technologie de votre site"
          </p>
        </div>

        {/* Option 1: Modern Card Style */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Option 1: Modern Card Style</h2>
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <div className="inline-flex items-center px-4 py-2 rounded-full bg-blue-100 border border-blue-200 text-blue-700 text-sm font-medium mb-6">
                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse mr-2"></div>
                  <Server className="w-4 h-4 mr-2" />
                  Analyse technique
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-4">
                  Technologie de votre site
                </h3>
                <p className="text-lg text-gray-600 mb-6">
                  Analyse du CMS et des technologies utilisées pour optimiser les performances
                </p>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-blue-100 text-blue-600">
                      <Code className="w-4 h-4" />
                    </div>
                    <span className="text-gray-700 font-medium">CMS détecté avec précision</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-green-100 text-green-600">
                      <Shield className="w-4 h-4" />
                    </div>
                    <span className="text-gray-700 font-medium">Recommandations de sécurité</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-orange-100 text-orange-600">
                      <Zap className="w-4 h-4" />
                    </div>
                    <span className="text-gray-700 font-medium">Optimisations suggérées</span>
                  </div>
                </div>
              </div>
              <div>
                <div className="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-2xl p-6 border border-blue-200">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 rounded-lg overflow-hidden border border-gray-200 flex-shrink-0">
                        <img 
                          src={mockCmsData.logo_url} 
                          alt="Logo WordPress" 
                          className="w-full h-full object-contain p-1"
                        />
                      </div>
                      <div>
                        <h4 className="text-lg font-bold text-gray-900">{mockCmsData.name}</h4>
                        <p className="text-sm text-gray-600">Version {mockCmsData.version}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-blue-600">{mockCmsData.confidence}%</div>
                      <div className="text-xs text-gray-500">Confiance</div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-start space-x-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-amber-800">Attention aux plugins non mis à jour et à la sécurité.</p>
                    </div>
                    <div className="flex items-start space-x-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <Shield className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <div className="text-sm text-green-800">
                        <p className="font-medium mb-1">Recommandations :</p>
                        <ul className="space-y-1 text-xs">
                          <li>• Mises à jour régulières</li>
                          <li>• Sauvegardes automatiques</li>
                          <li>• Sécurisation admin</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Option 2: Dashboard Analytics Style */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Option 2: Dashboard Analytics Style</h2>
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
            <div className="grid lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <div className="flex items-center gap-4 mb-6">
                  <div className="p-3 rounded-2xl bg-gradient-to-br from-purple-500 to-indigo-600 text-white">
                    <Server size={24} />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">Technologie de votre site</h3>
                    <p className="text-gray-600">Analyse du CMS et des technologies utilisées</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200">
                    <div className="flex items-center justify-between mb-2">
                      <Globe className="w-5 h-5 text-blue-600" />
                      <span className="text-xs font-medium text-blue-600 bg-blue-200 px-2 py-1 rounded-full">CMS</span>
                    </div>
                    <div className="text-2xl font-bold text-gray-900 mb-1">{mockCmsData.name}</div>
                    <div className="text-sm text-gray-600">Version {mockCmsData.version}</div>
                  </div>
                  
                  <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 border border-green-200">
                    <div className="flex items-center justify-between mb-2">
                      <BarChart3 className="w-5 h-5 text-green-600" />
                      <span className="text-xs font-medium text-green-600 bg-green-200 px-2 py-1 rounded-full">Score</span>
                    </div>
                    <div className="text-2xl font-bold text-gray-900 mb-1">{mockCmsData.confidence}%</div>
                    <div className="text-sm text-gray-600">Confiance</div>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Code className="w-4 h-4 text-blue-600" />
                    </div>
                    <span className="text-gray-700 font-medium">CMS populaire et puissant détecté</span>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                      <AlertTriangle className="w-4 h-4 text-amber-600" />
                    </div>
                    <span className="text-gray-700 font-medium">Attention aux mises à jour de sécurité</span>
                  </div>
                </div>
              </div>
              
              <div>
                <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl p-6 text-white">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-white rounded-lg p-2">
                      <img 
                        src={mockCmsData.logo_url} 
                        alt="Logo WordPress" 
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <div>
                      <div className="font-bold">WordPress</div>
                      <div className="text-sm text-gray-300">CMS détecté</div>
                    </div>
                  </div>
                  
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-300">Version</span>
                      <span className="font-medium">4.7.30</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">Confiance</span>
                      <span className="font-medium text-green-400">90%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">Sécurité</span>
                      <span className="font-medium text-amber-400">À surveiller</span>
                    </div>
                  </div>
                  
                  <div className="mt-4 pt-4 border-t border-gray-700">
                    <div className="text-xs text-gray-400 mb-2">Recommandations</div>
                    <div className="space-y-1 text-xs">
                      <div className="flex items-center gap-2">
                        <div className="w-1 h-1 bg-orange-400 rounded-full"></div>
                        <span>Mises à jour régulières</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1 h-1 bg-orange-400 rounded-full"></div>
                        <span>Sauvegardes automatiques</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Option 3: Tech Dashboard Style (Adapté aux couleurs TestPage) */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Option 3: Tech Dashboard Style (Couleurs TestPage)</h2>
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <div className="inline-flex items-center px-4 py-2 rounded-full bg-orange-100 border border-orange-200 text-orange-700 text-sm font-medium mb-6">
                  <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse mr-2"></div>
                  <Server className="w-4 h-4 mr-2" />
                  Analyse technique
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-4">
                  Technologie de <span className="text-orange-600">votre site</span>
                </h3>
                <p className="text-lg text-gray-600 mb-6">
                  Analyse du CMS et des technologies utilisées pour optimiser les performances et la sécurité
                </p>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-orange-100 text-orange-600">
                      <Code className="w-4 h-4" />
                    </div>
                    <span className="text-gray-700 font-medium">CMS détecté avec {mockCmsData.confidence}% de confiance</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-orange-100 text-orange-600">
                      <Shield className="w-4 h-4" />
                    </div>
                    <span className="text-gray-700 font-medium">Recommandations de sécurité incluses</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-orange-100 text-orange-600">
                      <Zap className="w-4 h-4" />
                    </div>
                    <span className="text-gray-700 font-medium">Optimisations techniques suggérées</span>
                  </div>
                </div>
              </div>
              <div>
                <div className="bg-gray-800 rounded-2xl p-6 shadow-xl border border-gray-700">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-white rounded-lg p-2 border border-gray-600">
                        <img 
                          src={mockCmsData.logo_url} 
                          alt="Logo WordPress" 
                          className="w-full h-full object-contain"
                        />
                      </div>
                      <div>
                        <h4 className="text-lg font-bold text-white">{mockCmsData.name}</h4>
                        <p className="text-sm text-gray-300">Version {mockCmsData.version}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-orange-400">{mockCmsData.confidence}%</div>
                      <div className="text-xs text-gray-400">Confiance</div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="bg-gray-700 rounded-lg p-3 border border-gray-600">
                      <div className="text-orange-400 text-sm font-medium mb-1">Sécurité</div>
                      <div className="text-white font-bold">À surveiller</div>
                    </div>
                    <div className="bg-gray-700 rounded-lg p-3 border border-gray-600">
                      <div className="text-orange-400 text-sm font-medium mb-1">Performance</div>
                      <div className="text-white font-bold">Optimisable</div>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex items-start space-x-3 p-3 bg-amber-900/30 border border-amber-700/50 rounded-lg">
                      <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-amber-200">Attention aux plugins non mis à jour</p>
                    </div>
                    <div className="flex items-start space-x-3 p-3 bg-green-900/30 border border-green-700/50 rounded-lg">
                      <Shield className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      <div className="text-sm text-green-200">
                        <p className="font-medium mb-1">Recommandations :</p>
                        <ul className="space-y-1 text-xs">
                          <li className="flex items-center gap-2">
                            <div className="w-1 h-1 bg-orange-400 rounded-full"></div>
                            <span>Mises à jour régulières</span>
                          </li>
                          <li className="flex items-center gap-2">
                            <div className="w-1 h-1 bg-orange-400 rounded-full"></div>
                            <span>Sauvegardes automatiques</span>
                          </li>
                          <li className="flex items-center gap-2">
                            <div className="w-1 h-1 bg-orange-400 rounded-full"></div>
                            <span>Sécurisation admin</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="text-center">
          <p className="text-gray-600 mb-4">
            Choisissez le design qui correspond le mieux à vos attentes
          </p>
          <div className="space-x-4">
            <button className="px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors">
              Implémenter Option 3
            </button>
            <button className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors">
              Retour à TestPage
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CmsInfoComparison;
